import React from 'react';
import { css } from '@emotion/react';
import MuiAccordion from '@mui/material/Accordion';
import MuiAccordionDetails from '@mui/material/AccordionDetails';
import MuiAccordionSummary from '@mui/material/AccordionSummary';
import { i18n_frequently_asked_questions } from '@yi/i18n/phrases/i18n_frequently_asked_questions';

import { useLocale } from '~/src/components/LocaleProvider';
import { breakpoints, paddingVariables, typography } from '~/src/styles';

import { getDataByLocale } from './getDataByLocale';
import { ReactComponent as IconExpand } from './icon-expand.svg';
import { commonStyles } from '../common-styles';

export const containerBackgroundColor =
  'linear-gradient(0deg, rgba(255, 243, 226, 0.50) 0%, rgba(255, 243, 226, 0.50) 100%), #FFF';
const styles = {
  container: css({
    display: 'grid',
    overflowX: 'hidden',
    gridTemplateAreas: `
        '.'
        'title'
        '.'
        'faq'
        '.'
    `,
    gridTemplateRows: `var(--Section-Padding) auto 16px auto var(--Section-Padding)`,
    [breakpoints.minTablet]: {
      gridTemplateAreas: `
        '. . .'
        '. title .'
        '. . .'
        '. faq .'
        '. . .'
    `,
      gridTemplateColumns: `1fr auto 1fr`,
      gridTemplateRows: `${paddingVariables.p80} auto 24px auto ${paddingVariables.p80}`,
      background: containerBackgroundColor,
    },
    [breakpoints.minDesktop]: {
      gridTemplateRows: `${paddingVariables.p120} auto 32px auto ${paddingVariables.p120}`,
    },
    '& .title': [
      commonStyles.contentPadding,
      typography.heading1,
      {
        gridArea: 'title',
        color: 'var(--Dark-Text, #27292D)',
        textTransform: 'capitalize',
        textAlign: `center`,
      },
    ],
  }),
  faq: css({
    gridArea: 'faq',
    paddingLeft: 'var(--Mobile-Horizontal-Padding)',
    paddingRight: 'var(--Mobile-Horizontal-Padding)',
    display: 'flex',
    flexDirection: 'column',
    gap: '16px',
    [breakpoints.minTablet]: {
      gap: '32px',
      margin: 'auto',
    },
    [breakpoints.minDesktop]: {
      gap: '24px',
    },
  }),
  question: css(typography.mobileBodyTextBold, {
    color: 'var(--Dark-Text, #27292D)',
    [breakpoints.minDesktop]: typography.desktopLargeBodyTextBold,
  }),
  answer: css(typography.mobileBodyText, {
    color: 'var(--Grey-Text, #585858)',
    [breakpoints.minDesktop]: typography.desktopLargeBodyText,
  }),
  faqItem: css({
    maxWidth: '970px',
    display: 'flex',
    padding: '16px',
    flexDirection: 'column',
    alignItems: 'flex-start',
    background: '#FFF',
    boxShadow: '4px 4px 20px 0px rgba(0, 0, 0, 0.08)',
    '&:before': {
      backgroundColor: 'transparent',
    },
    '&.Mui-expanded': {
      margin: '0',
    },
    '.MuiCollapse-hidden': {
      visibility: 'hidden',
    },
    '.MuiCollapse-root': {
      height: '0',
    },
  }),
  accordionSummary: css({
    width: '100%',
    minHeight: 'unset',
    gap: '12px',
    padding: '0',
    '.MuiAccordionSummary-content': {
      margin: '0',
    },
    '&.Mui-focusVisible': {
      background: 'none',
    },
    '&.Mui-expanded': {
      minHeight: 'unset',
    },
    '& .Mui-expanded': {
      color: '#000000',
      '&.MuiAccordionSummary-expandIconWrapper': {
        color: 'var(--Secondary)',
      },
    },
  }),
  accordionDetails: css({
    padding: '10px 0 8px 0',
  }),
  expandIcon: css({
    width: '18px',
  }),
};

export const FAQ: React.FC<{ className?: string }> = props => {
  const { locale } = useLocale();
  const data = getDataByLocale(locale);
  return (
    <section css={styles.container} {...props}>
      <h1 className="title">{i18n_frequently_asked_questions(locale)}</h1>
      <div css={styles.faq}>
        {data.map(item => (
          <MuiAccordion key={item.question} css={styles.faqItem}>
            <MuiAccordionSummary
              expandIcon={<IconExpand css={styles.expandIcon} />}
              css={styles.accordionSummary}
            >
              <div css={styles.question}>{item.question}</div>
            </MuiAccordionSummary>
            <MuiAccordionDetails css={styles.accordionDetails}>
              <div css={styles.answer}>{item.answer}</div>
            </MuiAccordionDetails>
          </MuiAccordion>
        ))}
      </div>
    </section>
  );
};
